/**
 * Section 4: Property Condition + Details
 */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { FileUpload } from '@ox/visits-ui/components/shared/FileUpload';
import { Input, Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';
import { CheckboxList } from '@ox/blox';

const APPLIANCES = ['Microwave', 'Stove', 'Oven', 'Refrigerator', 'Washer', 'Dryer', 'Dishwasher'];
const APPLIANCES_REPLACEMENT = [
  'Microwave',
  'Stove',
  'Oven',
  'Dishwasher',
  'Refrigerator',
  'Washer',
  'Dryer',
  'Utilities are not on, unable to confirm if appliances are functioning.',
  'None or Unsure',
];

export function Section4PropertyCondition() {
  const { formData, errors, updateField } = useForm();

  const handleUtilitiesChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('all_utilities_functioning', 'Other: ');
      } else {
        updateField('all_utilities_functioning', value);
      }
    },
    [updateField],
  );

  const handleUtilitiesOtherChange = useCallback(
    (value: string) => updateField('all_utilities_functioning', 'Other: ' + value),
    [updateField],
  );

  const handleOdorChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('odors_present', 'Other: ');
      } else {
        updateField('odors_present', value);
      }
    },
    [updateField],
  );

  const handleOdorOtherChange = useCallback(
    (value: string) => updateField('odors_present', 'Other: ' + value),
    [updateField],
  );

  const handleRoofDamageChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('visible_roof_damage', 'Other: ');
      } else {
        updateField('visible_roof_damage', value);
      }
    },
    [updateField],
  );

  const handleRoofDamageOtherChange = useCallback(
    (value: string) => updateField('visible_roof_damage', 'Other: ' + value),
    [updateField],
  );

  const handleWaterSuppliesChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('water_supplies_functional', 'Other: ');
      } else {
        updateField('water_supplies_functional', value);
      }
    },
    [updateField],
  );

  const handleWaterSuppliesOtherChange = useCallback(
    (value: string) => updateField('water_supplies_functional', 'Other: ' + value),
    [updateField],
  );

  const handleHomeDamageChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('visible_home_damage', 'Other: ');
      } else {
        updateField('visible_home_damage', value);
      }
    },
    [updateField],
  );

  const handleHomeDamageOtherChange = useCallback(
    (value: string) => updateField('visible_home_damage', 'Other: ' + value),
    [updateField],
  );

  const handleStreetAccessChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('street_access_restrictions', 'Other: ');
      } else {
        updateField('street_access_restrictions', value);
      }
    },
    [updateField],
  );

  const handleStreetAccessOtherChange = useCallback(
    (value: string) => updateField('street_access_restrictions', 'Other: ' + value),
    [updateField],
  );

  const handleAtticAccessChange = useCallback(
    (value: string) => {
      if (value === 'Other') {
        updateField('attic_crawl_space_accessible', 'Other: ');
      } else {
        updateField('attic_crawl_space_accessible', value);
      }
    },
    [updateField],
  );

  const handleAtticAccessOtherChange = useCallback(
    (value: string) => updateField('attic_crawl_space_accessible', 'Other: ' + value),
    [updateField],
  );

  const handleSprinklerSystemChange = useCallback(
    (value: string) => updateField('sprinkler_system', value),
    [updateField],
  );

  const handleHomeDamageTextChange = useCallback(
    (value: string) => updateField('any_home_damage', value),
    [updateField],
  );

  const handleCheckBoxListChange = useCallback(
    (e) => {
      updateField(e.target.name, e.target.value);
    }, [updateField]
  );

  return (
    <div className="form-section">
      <h2>Property Condition + Details</h2>
      <p>
        The following section has several write-in/multiple choice questions detailing the property
        condition as well as key property details. Please fill out this section to the best of your
        ability.
      </p>

      <CheckboxList
        label="Please list all appliances installed on the property"
        name="installed_appliances"
        options={APPLIANCES.map((appliance) => ({ label: appliance, value: appliance }))}
        onChange={handleCheckBoxListChange}
        defaultValue={formData.installed_appliances || []}
        required
        errorMessage={errors.installed_appliances}
      />

      <CheckboxList
        label="Please indicate which, if ANY, appliances need replacement/repair"
        name="appliances_need_replacement"
        options={APPLIANCES_REPLACEMENT.map((appliance) => ({ label: appliance, value: appliance }))}
        onChange={handleCheckBoxListChange}
        defaultValue={formData.appliances_need_replacement || []}
        required
        errorMessage={errors.appliances_need_replacement}
      />

      <Select
        label="Are all utilities (gas, electric, water) on and functioning as intended? If one or more utilities are not operational, please use the 'other' option to indicate which ones."
        name="all_utilities_functioning"
        value={
          formData.all_utilities_functioning?.startsWith('Other:')
            ? 'Other'
            : formData.all_utilities_functioning || ''
        }
        onChange={handleUtilitiesChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.all_utilities_functioning?.startsWith('Other:') && (
        <Input
          label="Specify which utilities"
          name="all_utilities_functioning_other"
          value={formData.all_utilities_functioning.replace('Other: ', '')}
          onChange={handleUtilitiesOtherChange}
          placeholder="Please specify which utilities..."
        />
      )}

      <Select
        label="Did you observe a sprinkler system on the property? (Please do NOT attempt to operate any automated sprinkler systems. Visual inspection ok!)"
        name="sprinkler_system"
        value={formData.sprinkler_system || ''}
        onChange={handleSprinklerSystemChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          {
            label: 'Yes, there is a sprinkler system.',
            value: 'Yes, there is a sprinkler system.',
          },
          {
            label: 'No, there is not a sprinkler system.',
            value: 'No, there is not a sprinkler system.',
          },
        ]}
        required
      />

      <Select
        label="Are there any odors present in the home? (If yes, provide details in 'Other')"
        name="odors_present"
        value={formData.odors_present?.startsWith('Other:') ? 'Other' : formData.odors_present || ''}
        onChange={handleOdorChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'No', value: 'No' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.odors_present?.startsWith('Other:') && (
        <Input
          label="Describe odors"
          name="odors_present_other"
          value={formData.odors_present.replace('Other: ', '')}
          onChange={handleOdorOtherChange}
          placeholder="Describe odors..."
        />
      )}

      <Select
        label="Was there any visible roof damage? (i.e at street level view, missing tiles, etc.) (If yes, provide details in 'Other')"
        name="visible_roof_damage"
        value={
          formData.visible_roof_damage?.startsWith('Other:')
            ? 'Other'
            : formData.visible_roof_damage || ''
        }
        onChange={handleRoofDamageChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'No', value: 'No' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.visible_roof_damage?.startsWith('Other:') && (
        <Input
          label="Describe damage"
          name="visible_roof_damage_other"
          value={formData.visible_roof_damage.replace('Other: ', '')}
          onChange={handleRoofDamageOtherChange}
          placeholder="Describe damage..."
        />
      )}

      <Select
        label="Were toilets, faucets, major water supplies etc., in working order? (If no, provide details in 'Other')"
        name="water_supplies_functional"
        value={
          formData.water_supplies_functional?.startsWith('Other:')
            ? 'Other'
            : formData.water_supplies_functional || ''
        }
        onChange={handleWaterSuppliesChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.water_supplies_functional?.startsWith('Other:') && (
        <Input
          label="Describe issues"
          name="water_supplies_functional_other"
          value={formData.water_supplies_functional.replace('Other: ', '')}
          onChange={handleWaterSuppliesOtherChange}
          placeholder="Describe issues..."
        />
      )}

      <Select
        label="Was there any visible home damage possibly related to dry rot, insects, etc.? (If yes, provide details in 'Other')"
        name="visible_home_damage"
        value={
          formData.visible_home_damage?.startsWith('Other:')
            ? 'Other'
            : formData.visible_home_damage || ''
        }
        onChange={handleHomeDamageChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'No', value: 'No' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.visible_home_damage?.startsWith('Other:') && (
        <Input
          label="Describe damage"
          name="visible_home_damage_other"
          value={formData.visible_home_damage.replace('Other: ', '')}
          onChange={handleHomeDamageOtherChange}
          placeholder="Describe damage..."
        />
      )}

      <Select
        label="Were there any restrictions to access related to street condition? (If yes, provide details in 'Other')"
        name="street_access_restrictions"
        value={
          formData.street_access_restrictions?.startsWith('Other:')
            ? 'Other'
            : formData.street_access_restrictions || ''
        }
        onChange={handleStreetAccessChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'No', value: 'No' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.street_access_restrictions?.startsWith('Other:') && (
        <Input
          label="Describe restrictions"
          name="street_access_restrictions_other"
          value={formData.street_access_restrictions.replace('Other: ', '')}
          onChange={handleStreetAccessOtherChange}
          placeholder="Describe restrictions..."
        />
      )}

      <Select
        label="Did attics or crawl spaces have adequate access? (Visual inspection acceptable) (If no, provide details in 'Other')"
        name="attic_crawl_space_accessible"
        value={
          formData.attic_crawl_space_accessible?.startsWith('Other:')
            ? 'Other'
            : formData.attic_crawl_space_accessible || ''
        }
        onChange={handleAtticAccessChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'Other', value: 'Other' },
        ]}
        required
      />
      {formData.attic_crawl_space_accessible?.startsWith('Other:') && (
        <Input
          label="Describe issues"
          name="attic_crawl_space_accessible_other"
          value={formData.attic_crawl_space_accessible.replace('Other: ', '')}
          onChange={handleAtticAccessOtherChange}
          placeholder="Describe issues..."
        />
      )}

      <Textarea
        label="Is there any obvious damage to the home that you would note if you were listing the home?"
        name="any_home_damage"
        value={formData.any_home_damage || ''}
        onChange={handleHomeDamageTextChange}
        placeholder="Describe any additional damage with location"
        autoSize
        required
        hintMessage="(These call outs should be in addition to the damage, if any, you identified in the previous questions). Examples include: mold, scuff marks in the paint, broken light fixtures, missing light bulbs, etc. Please provide location of damage with description. ex. Mold found under first floor bathroom sink."
      />

      <FileUpload
        label="Property Damage (Up to 10 Photos)"
        mediaType="property_damage_photo"
        sectionNumber={4}
        maxFiles={10}
        accept="image/*"
      />
    </div>
  );
}
